class DynamicCartCount {
  constructor() {
    this.cartCountWrapper = document.getElementById('cart-count-wrapper');
    this.updateTimeout = null;
    this.init();
  }

  init() {
    if (!this.cartCountWrapper) {
      console.warn('Cart count wrapper not found');
      return;
    }

    this.bindEvents();
    
    this.interceptCartRequests();
    
    this.observeCartForms();
  }

  bindEvents() {
    const events = [
      'cart:changed',
      'cart:added', 
      'cart:removed',
      'cart:quantity-updated',
      'cart:updated'
    ];

    events.forEach(event => {
      document.addEventListener(event, this.debounceUpdate.bind(this));
    });
  }

  debounceUpdate() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    this.updateTimeout = setTimeout(() => {
      this.updateCartCount();
    }, 100);
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const cart = await response.json();
        this.updateCartCountDisplay(cart.item_count);
      } else {
        await this.updateCartCountViaSection();
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
      await this.updateCartCountViaSection();
    }
  }

  async updateCartCountViaSection() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-count&_=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCartCount = doc.querySelector('.header-icons-anchor-cart-count');
        
        if (newCartCount) {
          const count = parseInt(newCartCount.textContent.trim()) || 0;
          this.updateCartCountDisplay(count);
        }
      }
    } catch (error) {
      console.error('Error updating cart count via section rendering:', error);
    }
  }

  updateCartCountDisplay(count) {
    if (!this.cartCountWrapper) return;
    
    const cartCountSpan = this.cartCountWrapper.querySelector('.header-icons-anchor-cart-count');
    if (cartCountSpan) {
      const currentCount = parseInt(cartCountSpan.textContent.trim()) || 0;
      
      if (currentCount !== count) {
        cartCountSpan.classList.add('updating');
        
        setTimeout(() => {
          cartCountSpan.textContent = count;
          
          if (count === 0) {
            cartCountSpan.classList.add('header-icons-anchor-cart-count--hide');
          } else {
            cartCountSpan.classList.remove('header-icons-anchor-cart-count--hide');
            cartCountSpan.classList.add('cart-count-updated');
          }
          
          setTimeout(() => {
            cartCountSpan.classList.remove('updating', 'cart-count-updated');
          }, 400);
        }, 150);
        
        const event = new CustomEvent('cart-count:updated', {
          detail: { 
            count, 
            previousCount: currentCount,
            changed: true
          }
        });
        document.dispatchEvent(event);
      }
    }
  }

  observeCartForms() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && form.action && this.isCartForm(form.action)) {
        setTimeout(() => this.debounceUpdate(), 500);
      }
    });
  }

  isCartForm(action) {
    return action && (
      action.includes('/cart/add') ||
      action.includes('/cart/update') ||
      action.includes('/cart/change') ||
      action.includes('/cart/clear')
    );
  }

  interceptCartRequests() {
    this.interceptFetch();
    this.interceptXHR();
  }

  interceptFetch() {
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(...args) {
      const [url, options] = args;
      const isCartRequest = typeof url === 'string' && self.isCartUrl(url);
      
      const fetchPromise = originalFetch.apply(this, args);
      
      if (isCartRequest) {
        fetchPromise.then((response) => {
          if (response.ok) {
            setTimeout(() => self.debounceUpdate(), 200);
          }
        }).catch(() => {
          setTimeout(() => self.debounceUpdate(), 500);
        });
      }
      
      return fetchPromise;
    };
  }

  interceptXHR() {
    const self = this;
    
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      this._url = url;
      return originalXHROpen.apply(this, [method, url, ...args]);
    };

    const originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function(...args) {
      const isCartRequest = this._url && self.isCartUrl(this._url);

      if (isCartRequest) {
        this.addEventListener('loadend', () => {
          if (this.status >= 200 && this.status < 300) {
            setTimeout(() => self.debounceUpdate(), 200);
          }
        });
      }

      return originalXHRSend.apply(this, args);
    };
  }

  isCartUrl(url) {
    return url && (
      url.includes('/cart/add') ||
      url.includes('/cart/update') ||
      url.includes('/cart/change') ||
      url.includes('/cart/clear') ||
      url.includes('/cart.js')
    );
  }

  refresh() {
    this.debounceUpdate();
  }

  destroy() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.dynamicCartCount = new DynamicCartCount();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId === 'header') {
    if (window.dynamicCartCount) {
      window.dynamicCartCount.destroy();
    }
    window.dynamicCartCount = new DynamicCartCount();
  }
});

document.addEventListener('shopify:section:reorder', () => {
  if (window.dynamicCartCount) {
    window.dynamicCartCount.refresh();
  }
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = DynamicCartCount;
}